import { ErrorHandler, Injectable } from '@angular/core';
import {
  AttendanceBonusApiService,
  AttendanceBonusModel
} from '@app/core/service/api/attendanceBonus.api.service';
import {
  TSpecBodyPostApproveAttendanceBonus,
  TSpecPathPostApproveAttendanceBonus
} from '@backend/types/generated/AttendanceBonuses/postApproveAttendanceBonus';
import {
  TSpecBodyPostExcuseAttendanceBonus,
  TSpecPathPostExcuseAttendanceBonus
} from '@backend/types/generated/AttendanceBonuses/postExcuseAttendanceBonus';
import {
  TSpecBodyPostRejectAttendanceBonus,
  TSpecPathPostRejectAttendanceBonus
} from '@backend/types/generated/AttendanceBonuses/postRejectAttendanceBonus';
import {
  Observable,
  Subject,
  catchError,
  of,
  repeat,
  shareReplay,
  tap
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AttendanceBonusListService {
  private readonly _pendingUpdate$ = new Subject<void>();

  public readonly pendingAttendanceBonuses$: Observable<
    AttendanceBonusModel[]
  > = this._attendanceBonusApi.getPendingAttendanceBonuses().pipe(
    catchError((error) => {
      this._errorHandler.handleError(error);
      return of([]);
    }),
    repeat({ delay: () => this._pendingUpdate$ }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public constructor(
    private readonly _attendanceBonusApi: AttendanceBonusApiService,
    private readonly _errorHandler: ErrorHandler
  ) {}

  public refreshPendingAttendanceBonuses(): void {
    this._pendingUpdate$.next();
  }

  public approveAttendanceBonus(params: {
    path: TSpecPathPostApproveAttendanceBonus;
    body: TSpecBodyPostApproveAttendanceBonus;
  }): Observable<AttendanceBonusModel> {
    return this._attendanceBonusApi
      .approveAttendanceBonus(params)
      .pipe(tap(() => this.refreshPendingAttendanceBonuses()));
  }

  public excuseAttendanceBonus(params: {
    path: TSpecPathPostExcuseAttendanceBonus;
    body: TSpecBodyPostExcuseAttendanceBonus;
  }): Observable<AttendanceBonusModel> {
    return this._attendanceBonusApi
      .excuseAttendanceBonus(params)
      .pipe(tap(() => this.refreshPendingAttendanceBonuses()));
  }

  public rejectAttendanceBonus(params: {
    path: TSpecPathPostRejectAttendanceBonus;
    body: TSpecBodyPostRejectAttendanceBonus;
  }): Observable<AttendanceBonusModel> {
    return this._attendanceBonusApi
      .rejectAttendanceBonus(params)
      .pipe(tap(() => this.refreshPendingAttendanceBonuses()));
  }
}
